import React, { ReactNode, useEffect, useId, useRef, useState } from "react";
import add_extensions_icon from "../images/add-extensions.svg";
import allowed_all_extensions_icon from "../images/allowed-all-extensions.svg";
import CaretClose from "../images/app-caret-close.svg?react";
import CaretOpen from "../images/app-caret-open.svg?react";
import { List, ListItem, ListItemText, Tooltip, SvgIcon, Box, Typography, Link } from "@mui/material";
import {
	CoreIconButton,
	CoreModal,
	CoreNoResultsText,
	CorePaper,
	CoreSearchField,
	CoreTitleCard,
	CoreTextField,
	CoreSearchFieldPaper,
} from "../../core";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";
import styles from "./applicationListModal.module.css";
import Add from "../images/app-add.svg?react";
import Remove from "../images/app-remove.svg?react";
import XCircle from "../images/x-circle-small.svg?react";
import ExtensionUnknown from "../images/extension-unknown.svg?react";
import { Extension } from "@verity/proctoring-option/types";
import i18n from "../../i18n/i18n";
import { Trans } from "react-i18next";

const PAGE_SIZE = 9;
const LIST_PANEL_HEIGHT = PAGE_SIZE * 49;

function ExtensionRow({ extension, action }: { extension: Extension; action: ReactNode }) {
	const textRef = useRef<HTMLDivElement>(null);
	const [overflow, setOverflow] = useState(false);

	useEffect(() => {
		if (textRef.current) {
			setOverflow(textRef.current.offsetWidth < textRef.current.scrollWidth);
		}
	}, [extension.name]);

	let name = (
		<ListItemText
			className={styles.applicationName}
			primaryTypographyProps={{
				ref: textRef,
				className: styles.applicationName,
			}}
			primary={extension.name}
		/>
	);
	if (overflow) {
		name = (
			<Tooltip
				title={extension.name}
				arrow
				placement="top"
				componentsProps={{
					tooltip: { style: { maxWidth: "500px" } },
				}}
			>
				<span>{name}</span>
			</Tooltip>
		);
	}

	return (
		<ListItem className={styles.applicationRow}>
			<div style={{ display: "flex", alignItems: "center" }}>
				{extension?.type !== "Other" && extension.iconSvg ? (
					<div style={{ marginTop: "4px" }} dangerouslySetInnerHTML={{ __html: extension.iconSvg }} />
				) : (
					<div style={{ marginTop: "4px" }}>
						<ExtensionUnknown style={{ width: "24px", height: "24px" }} />
					</div>
				)}
				<div style={{ display: "flex", flexDirection: "column", marginLeft: "13px", marginTop: "-5px" }}>
					<div>{name}</div>

					<div
						style={{
							fontFamily: "Inter",
							fontSize: 12,
							fontWeight: 800,
							color: "#757575",
							marginTop: "-7px",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{i18n.t("editQuizPage.configurationStep.extensionsAllowedListModal.extensionId")} {extension.id}
					</div>
				</div>
			</div>
			{action}
		</ListItem>
	);
}

function CategoryBlock({
	listAction,
	category,
	extensionList,
	onUpdate,
}: {
	listAction: "add" | "remove";
	category: "Writing Tools" | "Screen Capture Tools" | "Other";
	extensionList: Extension[];
	onUpdate: (extensions: Extension[]) => void;
}) {
	const [fold, setFold] = useState(false);
	const currentExtensionList = extensionList.filter((ext) => ext.type === category);

	if (currentExtensionList.length === 0) {
		return <></>;
	}
	const buttonIcon = listAction === "add" ? <Add /> : <Remove />;
	return (
		<>
			<ListItem className={styles.applicationRow}>
				<div style={{ display: "flex" }}>
					<SvgIcon
						onClick={() => setFold(!fold)}
						sx={{ color: "primary.main" }}
						style={{ cursor: "pointer", marginTop: "7px" }}
					>
						{fold ? <CaretClose /> : <CaretOpen />}
					</SvgIcon>
					<div style={{ marginTop: "3px" }}>
						<strong>{category}</strong>
					</div>
				</div>
				<CoreIconButton
					variant="plain"
					onClick={() => onUpdate(extensionList.filter((ext) => ext.type === category))}
					sx={{ color: "primary.main" }}
					aria-label={i18n.t(
						`editQuizPage.configurationStep.applicationBlockModal.ariaLabels.moveCategory.allow.${listAction}`,
						{ category },
					)}
				>
					{buttonIcon}
				</CoreIconButton>
			</ListItem>
			{!fold &&
				currentExtensionList.map((extension) => (
					<ExtensionRow
						key={extension.id}
						extension={extension}
						action={
							<CoreIconButton
								variant="plain"
								onClick={() => onUpdate([extension])}
								sx={{ color: "primary.main" }}
								aria-label={i18n.t(
									`editQuizPage.configurationStep.applicationBlockModal.ariaLabels.moveApp.allow.${listAction}`,
									{
										appName: extension.name,
									},
								)}
							>
								{buttonIcon}
							</CoreIconButton>
						}
					/>
				))}
		</>
	);
}

function ExtensionListTable({
	listAction,
	extensionList,
	onUpdate,
}: {
	listAction: "add" | "remove";
	extensionList: Extension[];
	onUpdate: (extensions: Extension[]) => void;
}) {
	return (
		<div className={styles.listPanel} style={{ gap: "20px" }}>
			<List className={styles.applicationList} sx={{ height: LIST_PANEL_HEIGHT }}>
				<CategoryBlock
					listAction={listAction}
					category="Writing Tools"
					extensionList={extensionList}
					onUpdate={onUpdate}
				/>
				<CategoryBlock
					listAction={listAction}
					category="Screen Capture Tools"
					extensionList={extensionList}
					onUpdate={onUpdate}
				/>
				<CategoryBlock listAction={listAction} category="Other" extensionList={extensionList} onUpdate={onUpdate} />
			</List>
		</div>
	);
}

function convertCurrentOptionToTwoList(extensionList: string[], extensions: Extension[]): [Extension[], Extension[]] {
	const blockedExtensionList: Extension[] = [];
	let availableExtensionList: Extension[] = [...extensions];
	for (const extension of extensionList) {
		const extensionId = extension.slice(-32);
		const extensionName = extension.slice(0, -32);
		const foundExtension = extensions.find((ext) => ext.id === extensionId);
		if (foundExtension) {
			if (blockedExtensionList.includes(foundExtension)) continue;
			blockedExtensionList.push(foundExtension);
			availableExtensionList = availableExtensionList.filter((ext) => ext.id !== foundExtension.id);
		} else {
			blockedExtensionList.push({
				id: extensionId,
				name: extensionName,
				type: "Other",
			});
		}
	}
	return [availableExtensionList, blockedExtensionList];
}

export default function ExtensionListModal({
	onClose,
	allExtensions,
	initialSelectedExtensionNames,
	fetchChromeExtensionData,
	onSave,
}: {
	onClose: () => void;
	allExtensions: Extension[];
	initialSelectedExtensionNames: string[];
	fetchChromeExtensionData: (id: string) => Promise<Extension | null>;
	onSave: (processNames: string[]) => void;
}) {
	const [showAddModal, setShowAddModal] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [initialUnselectedExtensions, initialSelectedExtensions] = convertCurrentOptionToTwoList(
		initialSelectedExtensionNames,
		allExtensions,
	);
	const [selectedExtensions, setSelectedExtensions] = useState<Extension[]>(initialSelectedExtensions);
	const [unselectedExtensions, setUnselectedExtensions] = useState<Extension[]>(initialUnselectedExtensions);
	const [saving, setSaving] = useState(false);
	const [changeMade, setChangeMade] = useState(false);
	const isMobileLayout = useMobileLayout();

	const searchFilter = searchText.toLowerCase();
	const sortFunction = (a: Extension, b: Extension) => a.name.localeCompare(b.name);
	const displayExtension = unselectedExtensions
		.filter((extension) => extension.id?.includes(searchFilter) || extension.name.toLowerCase().includes(searchFilter))
		.sort(sortFunction);
	const controlId = useId();

	return (
		<CoreModal
			open
			onClose={onClose}
			title={isMobileLayout ? undefined : i18n.t(`editQuizPage.configurationStep.extensionsAllowedListModal.title`)}
			minWidth={isMobileLayout ? undefined : "min(65%, 1000px)"}
			minHeight={isMobileLayout ? undefined : "min(90%, 875px)"}
			widthFixed
			heightFixed
			padding={isMobileLayout ? undefined : "0 12px 0 24px"}
			disableTopCloseButton={saving}
			fullScreen={isMobileLayout}
			showTopCloseButton={!isMobileLayout}
			dialogContentBackground={isMobileLayout ? "#f4f4f4" : undefined}
			buttons={[
				{
					text: i18n.t(`editQuizPage.configurationStep.extensionsAllowedListModal.cancel`),
					color: "secondary",
					style: { marginLeft: isMobileLayout ? "auto" : undefined },
					onClick: onClose,
					disabled: saving,
				},
				{
					text: saving
						? i18n.t(`editQuizPage.configurationStep.extensionsAllowedListModal.saving`)
						: i18n.t(`editQuizPage.configurationStep.extensionsAllowedListModal.save`),
					color: "primary",
					onClick: () => {
						setSaving(true);
						onSave(Array.from(new Set(selectedExtensions.map((extension) => extension.name + extension.id))));
						onClose();
					},
					disabled: saving || !changeMade,
				},
			]}
		>
			<ConditionalWrapper
				condition={isMobileLayout}
				whenTrue={(children) => (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							width: "100%",
							gap: "10px",
							background: "f4f4f4",
						}}
					>
						{children}
					</div>
				)}
			>
				{isMobileLayout && (
					<CoreTitleCard
						title={"Allowed Extension List"}
						description={"Add a list of extensions that are allowed during the assessment."}
						style={{ padding: "15px 10px 10px 10px", marginTop: "10px" }}
						slotProps={{ title: { style: { color: "#42296E" } }, text: { style: { gap: "5px" } } }}
					/>
				)}
				<ConditionalWrapper
					condition={isMobileLayout}
					whenTrue={(children) => (
						<CorePaper
							component="section"
							sx={{
								display: "flex",
								flexWrap: "wrap",
								justifyContent: "space-between",
								width: "100%",
								columnGap: "24px",
								padding: "10px",
								boxSizing: "border-box",
							}}
						>
							{children}
						</CorePaper>
					)}
				>
					{/* Text Content */}
					<div
						style={{
							fontSize: 15,
							fontWeight: 400,
							display: "flex",
							flexDirection: "column",
							gap: 16,
							padding: "20px 20px 0px 20px",
							justifyContent: "flex-start",
						}}
					>
						<div
							style={isMobileLayout ? { gap: "8px", width: "100%" } : undefined}
							onClick={(e) => {
								e.preventDefault();
								setShowAddModal(true);
							}}
						>
							<span>
								<Trans
									i18n={i18n}
									i18nKey="editQuizPage.configurationStep.extensionsAllowedListModal.description"
									components={{
										b: <span style={{ fontWeight: "bold" }} />,
										appLink: (
											<Link
												href="#"
												className={styles["application-link"]}
												onClick={(e) => {
													e.preventDefault();
													setShowAddModal(true);
												}}
											/>
										),
									}}
								/>
							</span>
						</div>
					</div>

					{/* UX Content */}
					<div
						style={
							isMobileLayout
								? {
										display: "flex",
										flexWrap: "wrap",
										justifyContent: "center",
										columnGap: "24px",
										maxWidth: "100%",
									}
								: {
										padding: "20px 20px",
										display: "flex",
										flexWrap: "wrap",
										justifyContent: "center",
										columnGap: "24px",
									}
						}
					>
						<div style={{ flexBasis: "100%", justifyContent: "center", display: "flex" }}>
							<CoreSearchFieldPaper>
								<CoreSearchField
									autoComplete="off"
									label={i18n.t("editQuizPage.configurationStep.extensionsAllowedListModal.search")}
									onSearch={(value) => setSearchText(value)}
									value={searchText}
									ariaControls={controlId}
									totalResults={displayExtension.length}
								/>
							</CoreSearchFieldPaper>
						</div>
						<div
							style={
								isMobileLayout
									? {
											justifyContent: "center",
											display: "flex",
											flexWrap: "wrap",
											width: "100%",
										}
									: {
											boxSizing: "border-box",
											flexBasis: "45%",
											display: "flex",
											flexWrap: "wrap",
											justifyContent: "space-between",
										}
							}
							id={controlId}
						>
							<Typography variant="h3" className={styles.header}>
								{i18n.t("editQuizPage.configurationStep.extensionsAllowedListModal.extensionList.title")}
							</Typography>
							{displayExtension.length === 0 ? (
								<div style={{ flexBasis: "100%" }}>
									<div className={styles.applicationList}>
										<div
											style={{
												display: "flex",
												flexWrap: "wrap",
												justifyContent: "center",
												alignContent: "center",
												gap: 15,
												height: LIST_PANEL_HEIGHT,
												padding: "0 20px",
											}}
										>
											<img src={allowed_all_extensions_icon} />
											<CoreNoResultsText
												header={i18n.t(
													`editQuizPage.configurationStep.extensionsAllowedListModal.extensionList.noExtensionLabel`,
												)}
												slotProps={{ header: { component: "div" } }}
											>
												<div
													style={isMobileLayout ? { gap: "8px", width: "100%" } : undefined}
													onClick={(e) => {
														e.preventDefault();
														setShowAddModal(true);
													}}
												>
													<span>
														<Trans
															i18n={i18n}
															i18nKey={`editQuizPage.configurationStep.extensionsAllowedListModal.extensionList.noExtensionDesc`}
															components={{
																appLink: (
																	<Link
																		href="#"
																		className={styles["application-link"]}
																		onClick={(e) => {
																			e.preventDefault();
																			setShowAddModal(true);
																		}}
																	/>
																),
															}}
														/>
													</span>
												</div>
											</CoreNoResultsText>
										</div>
									</div>
								</div>
							) : (
								<ExtensionListTable
									listAction="add"
									extensionList={displayExtension}
									onUpdate={(extensions) => {
										setSelectedExtensions(
											selectedExtensions.concat(extensions.filter((ext) => !selectedExtensions.includes(ext))),
										);
										setUnselectedExtensions(
											unselectedExtensions.filter((s) => extensions.every((ext) => ext.id !== s.id)),
										);
										setChangeMade(true);
									}}
								/>
							)}
						</div>
						<div
							style={
								isMobileLayout
									? {
											justifyContent: "center",
											display: "flex",
											flexWrap: "wrap",
											width: "100%",
										}
									: {
											boxSizing: "border-box",
											flexBasis: "45%",
											justifyContent: "start",
											display: "flex",
											flexWrap: "wrap",
											padding: "0px",
										}
							}
						>
							<Typography variant="h3" className={styles.header}>
								{i18n.t(`editQuizPage.configurationStep.extensionsAllowedListModal.selectedExtensionList.title`)}
							</Typography>
							{selectedExtensions.length === 0 ? (
								<div style={{ flexBasis: "100%" }}>
									<div className={styles.applicationList}>
										<div
											style={{
												display: "flex",
												flexWrap: "wrap",
												justifyContent: "center",
												alignContent: "center",
												gap: 15,
												height: LIST_PANEL_HEIGHT,
												padding: "0 20px",
											}}
										>
											<img src={add_extensions_icon} />
											<CoreNoResultsText
												header={i18n.t(
													`editQuizPage.configurationStep.extensionsAllowedListModal.selectedExtensionList.noExtensionLabel`,
												)}
												slotProps={{ header: { component: "div" } }}
											>
												{i18n.t(
													`editQuizPage.configurationStep.extensionsAllowedListModal.selectedExtensionList.noExtensionDesc`,
												)}
											</CoreNoResultsText>
										</div>
									</div>
								</div>
							) : (
								<ExtensionListTable
									listAction="remove"
									extensionList={selectedExtensions}
									onUpdate={(extensions) => {
										setUnselectedExtensions(
											unselectedExtensions.concat(extensions.filter((ext) => !unselectedExtensions.includes(ext))),
										);
										setSelectedExtensions(selectedExtensions.filter((s) => extensions.every((ext) => ext.id !== s.id)));
										setChangeMade(true);
									}}
								/>
							)}
						</div>
						{showAddModal && (
							<AddExtensionNameModal
								setShowAddModal={setShowAddModal}
								setBlockedExtensionList={setSelectedExtensions}
								fetchChromeExtensionData={fetchChromeExtensionData}
								setChangeMade={setChangeMade}
								blockedExtensionList={selectedExtensions}
							/>
						)}
					</div>
				</ConditionalWrapper>
			</ConditionalWrapper>
		</CoreModal>
	);
}

function AddExtensionNameModal({
	setShowAddModal,
	setBlockedExtensionList,
	fetchChromeExtensionData,
	setChangeMade,
	blockedExtensionList,
}: {
	setShowAddModal: React.Dispatch<React.SetStateAction<boolean>>;
	setBlockedExtensionList: React.Dispatch<React.SetStateAction<Extension[]>>;
	fetchChromeExtensionData: (id: string) => Promise<Extension | null>;
	setChangeMade: React.Dispatch<React.SetStateAction<boolean>>;
	blockedExtensionList: Extension[];
}) {
	const [extensionId, setExtensionId] = useState("");

	const [idError, setIdError] = useState(false);
	const isMobileLayout = useMobileLayout();

	const extractExtensionId = (input: string): string | null => {
		// If it's already a valid 32-char lowercase ID, return it
		if (/^[a-p]{32}$/.test(input)) {
			return input;
		}

		// Try to extract from a Chrome Webstore URL
		const match = input.match(/\/detail\/[^/]+\/([a-p]{32})/);
		return match ? match[1] : null;
	};

	return (
		<CoreModal
			open
			onClose={() => setShowAddModal(false)}
			title={i18n.t(`editQuizPage.configurationStep.extensionsAllowedListModal.addCustom.title`)}
			minWidth={isMobileLayout ? "100%" : "min(60%, 600px)"}
			widthFixed
			hideHorizontalOverflow
			hideVerticalOverflow
			contentMatchModalHeight
			padding={isMobileLayout ? "10px 24px" : undefined}
			buttons={[
				{
					text: i18n.t(`editQuizPage.configurationStep.extensionsAllowedListModal.cancel`),
					color: "secondary",
					onClick: () => setShowAddModal(false),
				},
				{
					text: i18n.t(`editQuizPage.configurationStep.extensionsAllowedListModal.add`),
					color: "primary",
					onClick: async () => {
						const extractedId = extractExtensionId(extensionId);

						if (!extractedId) {
							setIdError(true);
							return;
						}

						const extension_info = await fetchChromeExtensionData(extractedId);
						if (!extension_info || extension_info.name === "Chrome Web Store") {
							setIdError(true);
							return;
						}

						setBlockedExtensionList([
							...blockedExtensionList,
							{
								id: extension_info.id,
								name: extension_info.name,
								iconSvg: extension_info.iconSvg,
								type: "Other",
							},
						]);
						setShowAddModal(false);
						setChangeMade(true);
					},
					disabled: extensionId === "",
				},
			]}
		>
			<Trans
				i18n={i18n}
				i18nKey="editQuizPage.configurationStep.extensionsAllowedListModal.addCustom.description1"
				components={{
					a: (
						<Link
							href="https://support.yuja.com/hc/en-us/articles/36314216257943-Locating-a-Browser-Extension-URL"
							target="_blank"
							rel="noopener noreferrer"
							className={styles["application-link"]}
						/>
					),
				}}
			/>

			<Box sx={{ mt: 2 }}>
				<Typography
					sx={{
						fontFamily: "Inter",
						fontWeight: 600,
						fontStyle: "normal",
						fontSize: "15px",
						lineHeight: "150%",
						letterSpacing: "0px",
						color: "#000000",
						mb: 1,
					}}
				>
					{i18n.t(`editQuizPage.configurationStep.extensionsAllowedListModal.addCustom.inputTitle`)}
				</Typography>
				<CoreTextField
					value={extensionId}
					onChange={(e) => {
						setExtensionId(e.target.value.trim());
						setIdError(false);
					}}
					error={idError && extensionId.trim() !== ""}
					sx={{ width: "100%" }}
					onPaste={(event) => {
						event.preventDefault();
						const pasted = event.clipboardData.getData("text").trim();
						setExtensionId(pasted);
						setIdError(false);
					}}
					placeholder={i18n.t(`editQuizPage.configurationStep.extensionsAllowedListModal.addCustom.inputPlaceholder`)}
				/>
				<div className={styles.errorWrapper}>
					{idError && extensionId.trim() !== "" && (
						<div className={styles.errorContainer}>
							<XCircle fill="#D32F2F" style={{ marginTop: 4 }} />

							<div className={styles.errorText} style={{ paddingTop: "1px" }}>
								{i18n.t("editQuizPage.configurationStep.extensionsAllowedListModal.idErrorMsg")}
							</div>
						</div>
					)}
				</div>
			</Box>
		</CoreModal>
	);
}
